import { IStyle } from 'fela';
import { useContext } from 'react';
import { RendererContext } from 'react-fela';
import { ImageX } from '@bridebook/ui';
import componentStyles from './grid-gallery.style';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import { getIsDesktop } from '@bridebook/web/app-basic/lib/app/selectors';
import { noop } from 'remeda';

interface IPhoto {
  path: string;
  portrait?: boolean;
}

interface IProps {
  photos: IPhoto[];
  onImageClick?: () => void;
}

/*
On the media summary view, photos should be rendered in order but grouped together. Grouping together images can get tricky.
The main goal of this view is to present multiple images to the user at the same time, so they can quickly get an overview of the supplier.
The orientation of the images can be portrait or landscape. To show as much as we can from the images, we have to group them based on their orientation.
Three of these images can be grouped together and rendered in a specific layout. There are exceptions though.
For example three portraits are not allowed to be grouped together, so we should only group the first two and apply the same logic for the rest of the images.
We try to avoid reordering the images, because the suppliers may have uploaded them in the order they want them to appear.
They might be ordered by a theme. The only exception is when the last three images are portrait-landscape-portrait.
In this case we have no other way of rendering them, but putting the landscape after the second portrait.
We are doing this, so we avoid a portrait being rendered on it's own, because they could take away almost the full screen.
If there is only one portrait in the list, there is not much we can do about that.
This is partially the reason why we have a single portrait and landscape rendering at the end of the logic, so if the logic is flawed for some reason every image will be rendered anyway.
It is better to render them wrong than not rendering them at all.
*/
const GridGallery = ({ photos, onImageClick = noop }: IProps) => {
  const isDesktop = useSelector(getIsDesktop);

  const renderer = useContext(RendererContext);
  const styles = componentStyles();

  const gridClassName = renderer.renderRule(() => styles.grid as IStyle, {});

  const imageWidth = isDesktop ? 720 : 360;
  const landscapeImageHeight = isDesktop ? 514 : 257;
  const portraitImageHeight = isDesktop ? 1008 : 504;

  const renderRow = (index: number, content: JSX.Element) => {
    return (
      <div key={index} style={styles.row(index)}>
        {content}
      </div>
    );
  };

  const renderLandscape = (index: number) => (
    <div style={styles.landscape} onClick={onImageClick}>
      <ImageX
        src={photos[index].path}
        w={imageWidth}
        h={landscapeImageHeight}
        fit="fill"
        style={styles.imageContainer}
        imageStyle={styles.image}
        loading="lazy"
      />
    </div>
  );

  const renderPortrait = (index: number) => (
    <div style={styles.portrait} onClick={onImageClick}>
      <ImageX
        src={photos[index].path}
        w={imageWidth}
        h={portraitImageHeight}
        fit="fill"
        style={styles.imageContainer}
        imageStyle={styles.image}
        loading="lazy"
      />
    </div>
  );

  const groups: JSX.Element[] = [];

  let i = 0;
  let row = 1;

  const isLandscape = (index: number) => !photos[index].portrait;
  const isPortrait = (index: number) => photos[index].portrait;

  const matchLLL = () => isLandscape(i) && isLandscape(i + 1) && isLandscape(i + 2);
  const matchPPP = () => isPortrait(i) && isPortrait(i + 1) && isPortrait(i + 2);
  const matchLLP = () => isLandscape(i) && isLandscape(i + 1) && isPortrait(i + 2);
  const matchPLL = () => isPortrait(i) && isLandscape(i + 1) && isLandscape(i + 2);
  const matchLPP = () => isLandscape(i) && isPortrait(i + 1) && isPortrait(i + 2);
  const matchPLP = () => isPortrait(i) && isLandscape(i + 1) && isPortrait(i + 2);
  const matchLPL = () => isLandscape(i) && isPortrait(i + 1) && isLandscape(i + 2);
  const matchLL = () => isLandscape(i) && isLandscape(i + 1);
  const matchLP = () => isLandscape(i) && isPortrait(i + 1);
  const matchPP = () => isPortrait(i) && isPortrait(i + 1);
  const matchPL = () => isPortrait(i) && isLandscape(i + 1);

  while (i < photos.length) {
    // match the end for the rare edge case
    if (i === photos.length - 3) {
      if (matchPLP()) {
        // the only situation where we do reorder a little bit to prevent a portrait being on it's own

        const portrait1 = i++;
        const landscape = i++;
        const portrait2 = i++;
        groups.push(renderRow(row++, renderLandscape(landscape)));
        groups.push(
          renderRow(
            row++,
            <div className={gridClassName}>
              <div style={styles.gridArea_PLP_Left}>{renderPortrait(portrait1)}</div>
              <div style={styles.gridArea_PLP_Right}>{renderPortrait(portrait2)}</div>
            </div>,
          ),
        );
        continue;
      }
    }

    // match any
    // if there are 4 photos left and it ends with a portrait we shouldn't group three of them, because the last portrait would be on it's own, so we should group only two
    if (i + 2 < photos.length && !(i === photos.length - 4 && isPortrait(i + 3))) {
      if (matchLLL()) {
        groups.push(renderRow(row++, renderLandscape(i++)));
        groups.push(
          renderRow(
            row++,
            <div className={gridClassName}>
              <div style={styles.gridArea_LLL_Left}>{renderLandscape(i++)}</div>
              <div style={styles.gridArea_LLL_Right}>{renderLandscape(i++)}</div>
            </div>,
          ),
        );
        continue;
      }
      if (matchPPP()) {
        groups.push(
          renderRow(
            row++,
            <div className={gridClassName}>
              <div style={styles.gridArea_PPP_Left}>{renderPortrait(i++)}</div>
              <div style={styles.gridArea_PPP_RightTop}>{renderPortrait(i++)}</div>
              <div style={styles.gridArea_PPP_RightBottom}>{renderPortrait(i++)}</div>
            </div>,
          ),
        );
        continue;
      }
      if (matchLLP()) {
        groups.push(
          renderRow(
            row++,
            <div className={gridClassName}>
              <div style={styles.gridArea_LLP_LeftTop}>{renderLandscape(i++)}</div>
              <div style={styles.gridArea_LLP_LeftBottom}>{renderLandscape(i++)}</div>
              <div style={styles.gridArea_LLP_Right}>{renderPortrait(i++)}</div>
            </div>,
          ),
        );
        continue;
      }
      if (matchPLL()) {
        groups.push(
          renderRow(
            row++,
            <div className={gridClassName}>
              <div style={styles.gridArea_PLL_Left}>{renderPortrait(i++)}</div>
              <div style={styles.gridArea_PLL_RightTop}>{renderLandscape(i++)}</div>
              <div style={styles.gridArea_PLL_RightBottom}>{renderLandscape(i++)}</div>
            </div>,
          ),
        );
        continue;
      }
      if (matchLPP()) {
        groups.push(renderRow(row++, renderLandscape(i++)));
        groups.push(
          renderRow(
            row++,
            <div className={gridClassName}>
              <div style={styles.gridArea_LPP_Left}>{renderPortrait(i++)}</div>
              <div style={styles.gridArea_LPP_Right}>{renderPortrait(i++)}</div>
            </div>,
          ),
        );
        continue;
      }
      if (matchLPL()) {
        groups.push(renderRow(row++, renderLandscape(i++)));
        groups.push(
          renderRow(
            row++,
            <div className={gridClassName}>
              <div style={styles.gridArea_LPL_Left}>{renderPortrait(i++)}</div>
              <div style={styles.gridArea_LPL_Right}>{renderLandscape(i++)}</div>
            </div>,
          ),
        );
        continue;
      }
      // We do not handle PLP and PPL use cases on purpose. PPL could mess up the flow and PLP requires reordering which we should avoid if possible, except at the end.
    }
    if (i + 1 < photos.length) {
      if (matchLL()) {
        groups.push(
          renderRow(
            row++,
            <div className={gridClassName}>
              <div style={styles.gridArea_LL_Left}>{renderLandscape(i++)}</div>
              <div style={styles.gridArea_LL_Right}>{renderLandscape(i++)}</div>
            </div>,
          ),
        );
        continue;
      }
      if (matchPP()) {
        groups.push(
          renderRow(
            row++,
            <div className={gridClassName}>
              <div style={styles.gridArea_PP_Left}>{renderPortrait(i++)}</div>
              <div style={styles.gridArea_PP_Right}>{renderPortrait(i++)}</div>
            </div>,
          ),
        );
        continue;
      }
      if (matchLP()) {
        groups.push(
          renderRow(
            row++,
            <div className={gridClassName}>
              <div style={styles.gridArea_LP_Left}>{renderLandscape(i++)}</div>
              <div style={styles.gridArea_LP_Right}>{renderPortrait(i++)}</div>
            </div>,
          ),
        );
        continue;
      }
      if (matchPL()) {
        groups.push(
          renderRow(
            row++,
            <div className={gridClassName}>
              <div style={styles.gridArea_PL_Left}>{renderPortrait(i++)}</div>
              <div style={styles.gridArea_PL_Right}>{renderLandscape(i++)}</div>
            </div>,
          ),
        );
        continue;
      }
    }
    // Portrait should only happen, if there is only one portrait image in the gallery
    if (isPortrait(i)) {
      groups.push(renderRow(row++, renderPortrait(i++)));
      continue;
      // This landscape should only happen, if we are at the end and a landscape image is the last one
    } else {
      groups.push(renderRow(row++, renderLandscape(i++)));
      continue;
    }
  }

  return <>{groups}</>;
};

export default GridGallery;
